import {
  GitCommitIcon,
  PlusIcon,
  Trash01Icon,
  XIcon,
} from '@langchain/untitled-ui-icons';
import { Checkbox, CircularProgress, Modal, ModalDialog } from '@mui/joy';

import { Dispatch, SetStateAction, useState } from 'react';

import { Button, IconButton, Input, Text, Tooltip } from '@/design-system';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { useHostPatchProjectMutation } from '@/hooks/useSwr';
import { HostProjectSchema } from '@/types/schema.ts';
import { singleOriginEnabled } from '@/utils/constants.tsx';

import { SecretPlainInput } from '../SecretInput';

interface RevisionCrudModalProps {
  hostProject: HostProjectSchema;
  deploymentPlatformId: string;
  isOpen: boolean;
  doClose: () => void;
  customHeaders: Record<string, string>;
  setCustomHeaders: Dispatch<SetStateAction<Record<string, string>>>;
}

export function HostProjectSettingsModal({
  hostProject,
  deploymentPlatformId,
  isOpen,
  doClose,
  customHeaders,
  setCustomHeaders,
}: RevisionCrudModalProps) {
  const { value: langGraphRemoteReconcilerEnabled } = useOrgConfig(
    OrgConfigs.langgraph_remote_reconciler_enabled
  );
  const {
    id: hostProjectId,
    repo_branch: currentRepoCommit,
    build_on_push: currentBuildOnPush,
    custom_url: currentCustomURL,
    metadata: { image_source: imageSource },
  } = hostProject;

  const [repoCommit, setRepoCommit] = useState<string | null>(
    currentRepoCommit || null
  );
  const [buildOnPush, setBuildOnPush] = useState<boolean>(currentBuildOnPush);
  const [customUrl, setCustomUrl] = useState<string | null>(
    currentCustomURL || null
  );
  const hasProjectChanges =
    repoCommit !== currentRepoCommit ||
    buildOnPush !== currentBuildOnPush ||
    customUrl !== currentCustomURL;
  const patchProject = useHostPatchProjectMutation(hostProjectId || '');

  const [localHeaders, setLocalHeaders] = useState(customHeaders);
  const onChangeCustomHeaders = ({
    oldKey,
    newKey,
    value,
  }: {
    oldKey: string;
    newKey?: string;
    value?: string;
  }) => {
    setLocalHeaders((prev) => {
      if (value === undefined) return prev;
      // if key is updated, delete the old key and add the new key with the new value
      if (newKey !== undefined) {
        const newHeaders = { ...prev };
        delete newHeaders[oldKey];
        newHeaders[newKey] = value;
        return newHeaders;
      }
      // Otherwise, update the header
      return {
        ...prev,
        [oldKey]: value,
      };
    });
  };
  const addHeadersDisabledMessage = (() => {
    if (Object.keys(localHeaders).some((key) => key === '')) {
      return 'Header name cannot be empty';
    }
    if (Object.values(localHeaders).some((value) => value === '')) {
      return 'Header value cannot be empty';
    }
    return null;
  })();

  return (
    <Modal open={isOpen} onClose={doClose} title="New Revision">
      <ModalDialog variant="outlined" sx={{ minWidth: 750, overflowY: 'auto' }}>
        <div className="flex flex-col items-start gap-3">
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              <div className="inline-flex items-center justify-center rounded-full bg-brand-tertiary p-1">
                <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-brand-green-500 to-cyan-400 text-white">
                  <GitCommitIcon className="size-4 fill-white" />
                </div>
              </div>
              <span className="text-xl font-semibold">Settings</span>
            </div>

            <IconButton
              icon={XIcon}
              label="Close"
              variant="plain"
              color="secondary"
              onClick={doClose}
            />
          </div>
        </div>

        <div className="h-5" />

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            setCustomHeaders(localHeaders);
            if (hasProjectChanges) {
              const project = await patchProject.trigger({
                repo_branch: repoCommit ?? undefined,
                build_on_push: buildOnPush,
                custom_url: customUrl ?? undefined,
              });
              if (project) {
                doClose();
                return;
              }
            }
            doClose();
          }}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1">
            <Text variant="h2">Deployment settings</Text>
            <Text variant="sm">Applied globally to the deployment.</Text>
          </div>
          {(imageSource ?? 'github') === 'github' && (
            <>
              <Input
                label="Git Branch"
                onChange={(value) => setRepoCommit(value)}
                value={repoCommit ?? ''}
              />
              <Checkbox
                checked={buildOnPush}
                onChange={(e) => setBuildOnPush(e.target.checked)}
                label="Automatically update deployment on push to branch"
              />
            </>
          )}

          {(deploymentPlatformId === 'aws_ecs' ||
            langGraphRemoteReconcilerEnabled ||
            singleOriginEnabled) && (
            <Input
              label="Custom URL (metadata only)"
              onChange={(value) => setCustomUrl(value)}
              value={customUrl ?? undefined}
            />
          )}

          <div className="flex flex-col gap-1 pt-4">
            <Text variant="h2">Local settings</Text>
            <Text variant="sm">
              Stored locally in your browser only apply to you.
            </Text>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <Text variant="md" weight="medium">
                Custom headers
              </Text>
              <Text variant="sm">
                Applied to all connections to this deployment's data plane.
              </Text>
            </div>
            <div className="flex flex-col gap-2">
              {Object.entries(localHeaders).map(([key, value]) => (
                <div className="flex w-full items-center gap-2">
                  <Input
                    type="text"
                    value={key}
                    onChange={(newKey) =>
                      onChangeCustomHeaders({ oldKey: key, newKey, value })
                    }
                    debounceMs={500}
                    placeholder="Type name..."
                    required
                  />

                  <SecretPlainInput
                    type="text"
                    value={value}
                    onChange={(e) =>
                      onChangeCustomHeaders({
                        oldKey: key,
                        value: e.target.value,
                      })
                    }
                    placeholder="Type value..."
                    required
                    className="min-w-[50%]"
                  />

                  <IconButton
                    icon={Trash01Icon}
                    label="Delete"
                    onClick={() => {
                      setLocalHeaders((prev) => {
                        const newHeaders = { ...prev };
                        delete newHeaders[key];
                        return newHeaders;
                      });
                    }}
                    variant="outlined"
                    color="secondary"
                    size="md"
                  />
                </div>
              ))}
            </div>
            <Tooltip title={addHeadersDisabledMessage} side="bottom">
              <div className="w-fit">
                <Button
                  type="button"
                  onClick={() =>
                    onChangeCustomHeaders({
                      oldKey: '',
                      value: '',
                    })
                  }
                  variant="outlined"
                  color="secondary"
                  leftDecorator={PlusIcon}
                  disabled={!!addHeadersDisabledMessage}
                >
                  Add header
                </Button>
              </div>
            </Tooltip>
          </div>

          <div className="ml-auto flex flex-row gap-3">
            <Button variant="outlined" color="secondary" onClick={doClose}>
              Cancel
            </Button>
            {patchProject.isMutating ? (
              <CircularProgress size="sm" />
            ) : (
              <Button type="submit" color="primary">
                Save
              </Button>
            )}
          </div>
        </form>
      </ModalDialog>
    </Modal>
  );
}
